// Sprint 5.7 (D31) — chat-header chip showing the agent's git branch.
//
// Render contract:
//   `⎇ <branch> (· N ahead)? (· N behind)? (· •)?`
//
//   • Icon: Lucide `GitBranch`, 12 px, `--fg-subtle`.
//   • Branch name: 12 px JetBrains Mono, `--fg-muted`.
//   • ahead/behind: 11 px JetBrains Mono, `--fg-subtle`. Hidden at 0.
//   • Dirty dot: 5.5 px circle, `--status-warning`. Hidden when clean.
//   • Tooltip: full worktree path so Santi can verify the agent is in the
//     expected directory (Chatting Wizard CRM vs. a fork).
//   • Hidden entirely (returns null) when gitStatus is null — e.g. non-git
//     workdir, first poll not yet returned, or a git command timed out.
//
// Position: chat header (per Santi's explicit decision — reading the
// branch matters more than config chips do once a session is running).

import { Icon } from './atoms/Icon';
import { useTranslation } from '../i18n';
import type { GitStatus } from '../types';

interface BranchChipProps {
  gitStatus: GitStatus | null | undefined;
}

export function BranchChip({ gitStatus }: BranchChipProps) {
  const { t } = useTranslation();
  if (!gitStatus) return null;

  const { branch, ahead, behind, dirty, worktreePath } = gitStatus;

  // Keep the tooltip terse; multi-line is awkward on Safari mobile. Path
  // first (what matters for verification), counts second.
  const tooltipParts = [worktreePath];
  if (ahead > 0) tooltipParts.push(t('branch.ahead', { count: ahead }));
  if (behind > 0) tooltipParts.push(t('branch.behind', { count: behind }));
  if (dirty) tooltipParts.push(t('branch.uncommittedChanges'));
  const tooltip = tooltipParts.join(' · ');

  return (
    <span
      className="inline-flex items-center gap-1 max-w-[24ch] truncate"
      data-testid="branch-chip"
      title={tooltip}
    >
      <Icon
        name="GitBranch"
        size={12}
        className="shrink-0"
        style={{ color: 'var(--fg-subtle)' }}
        aria-hidden
      />
      <span
        className="font-mono text-caption truncate"
        style={{ color: 'var(--fg-muted)' }}
      >
        {branch}
      </span>
      {ahead > 0 && (
        <span
          className="font-mono text-caption tabular-nums shrink-0"
          style={{ color: 'var(--fg-subtle)' }}
          data-testid="branch-chip-ahead"
          aria-label={t('branch.ahead', { count: ahead })}
        >
          · {ahead}↑
        </span>
      )}
      {behind > 0 && (
        <span
          className="font-mono text-caption tabular-nums shrink-0"
          style={{ color: 'var(--fg-subtle)' }}
          data-testid="branch-chip-behind"
          aria-label={t('branch.behind', { count: behind })}
        >
          · {behind}↓
        </span>
      )}
      {dirty && (
        // EXCEPTION: 5.5 px is a Cursor-mirror exact size for the
        // dirty-branch indicator dot (Layer 4 has no half-pixel role).
        // Color goes via the `--status-warning` token.
        <span
          className="shrink-0 rounded-full"
          style={{
            width: 5.5,
            height: 5.5,
            backgroundColor: 'var(--status-warning)',
          }}
          data-testid="branch-chip-dirty"
          aria-label={t('branch.uncommittedChanges')}
        />
      )}
    </span>
  );
}
